import { TaskSubTicketLogResource } from 'dy-frontend-http-repository/lib/modules/TaskSubTicket/resources';
import moment from 'moment';
import React, { useState } from 'react';

import DevText from '@app/components/Text';
import { taskSubTicketStatusInformation } from '@app/data/consts';
import { Button, Callout, Divider, Intent, Spinner } from '@blueprintjs/core';
import Flex from '@components/Flex';

import { SubTicketDetailState } from '../../store/states';
import { selectRecentSubTicketLogs } from '../../utils';

export interface SubTicketDetailPanelProps {
    // Per sub-ticket, so one phase failing never leaves another panel spinning forever
    state: SubTicketDetailState | null;
    onRetry: () => void;
}

export type Props = SubTicketDetailPanelProps;

const formatDateTime = (value: string) => moment(value).format('D MMM YYYY, HH:mm');

// Everything in this panel is read-only. The two operational regions live in the workspace
// below it, so expanding a phase to read its history can never be a way to change it.
const SubTicketDetailPanel: React.FC<Props> = ({ state, onRetry }) => {
    // Collapsed by default and reset by unmounting with the panel, so re-expanding a phase always
    // opens on the same three entries rather than on whatever was last left open
    const [isHistoryExpanded, setIsHistoryExpanded] = useState(false);

    if (!state || (state.status === 'loading' && !state.detail)) {
        return (
            <Flex className="mt-2" justify="center">
                <Spinner size={20} />
            </Flex>
        );
    }

    if (state.status === 'error' && !state.detail) {
        return (
            <Callout className="mt-2" intent={Intent.WARNING} title="Detail could not be loaded">
                <Button className="mt-1" small onClick={onRetry}>
                    Retry
                </Button>
            </Callout>
        );
    }

    const detail = state.detail;
    if (!detail) {
        return null;
    }

    const { sub_ticket: subTicket, logs } = detail;

    // Neither Scope nor Due lives here any more. The scope summary is the card's title and the
    // due date is in the card's meta row, so repeating them below an expand chevron only makes
    // the reader check whether the two copies agree.
    const renderDeliverables = () => (
        <div className="mt-2">
            <DevText muted className="mb-1">
                Deliverables
            </DevText>

            {subTicket.expected_deliverables.length === 0 ?
                <DevText muted>None recorded</DevText>
            :   <ol className="mt-0 mb-0">
                    {subTicket.expected_deliverables.map((deliverable, index) => (
                        <li key={`${index}-${deliverable}`}>
                            <DevText inline>{deliverable}</DevText>
                        </li>
                    ))}
                </ol>
            }
        </div>
    );

    const renderLogEntry = (log: TaskSubTicketLogResource) => {
        const actor = `${log.actor_user.first_name} ${log.actor_user.last_name}`;

        const renderChange = () => {
            if (log.kind === 'status' && log.to_status) {
                const from = log.from_status ? taskSubTicketStatusInformation[log.from_status].title : 'nothing';
                return `changed status from ${from} to ${taskSubTicketStatusInformation[log.to_status].title}`;
            }

            if (log.kind === 'assignee') {
                return 'changed the assigned designer';
            }

            switch (log.kind) {
                case 'created':
                    return 'created this phase';
                case 'updated':
                    return 'updated the scope, deliverables or due date';
                case 'archived':
                    return 'archived this phase';
                case 'restored':
                    return 'restored this phase';
                default:
                    return 'changed this phase';
            }
        };

        return (
            <Flex key={log.id} className="mt-1" direction="row" align="center" justify="space-between">
                <DevText>
                    <DevText inline>{actor}</DevText>{' '}
                    <DevText inline muted>
                        {renderChange()}
                    </DevText>
                </DevText>
                <DevText muted small>
                    {formatDateTime(log.created_at)}
                </DevText>
            </Flex>
        );
    };

    // A phase that has been reassigned and re-statused a dozen times buries everything under it,
    // and the entries anybody opens the panel for are the most recent ones. So the panel opens on
    // the last three — in chronological order, the same direction the full list reads — and the
    // rest are one click away. With three or fewer there is nothing to reveal, so no toggle is
    // offered at all: a control that does nothing is worse than no control.
    const renderHistory = () => {
        const { visible, hiddenCount } = selectRecentSubTicketLogs(logs);
        const shown = isHistoryExpanded ? logs : visible;

        const renderToggle = () => {
            if (hiddenCount === 0) {
                return null;
            }

            return (
                <Button className="mt-1" minimal small onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}>
                    {isHistoryExpanded ? 'Show less' : `Show ${hiddenCount} earlier activities`}
                </Button>
            );
        };

        return (
            <div className="mt-2">
                <DevText muted className="mb-1">
                    History
                </DevText>

                {logs.length === 0 ?
                    <DevText muted>No changes recorded yet</DevText>
                :   shown.map(renderLogEntry)}

                {renderToggle()}
            </div>
        );
    };

    return (
        <div className="mt-2">
            <Divider className="mb-2" />
            {renderDeliverables()}
            {renderHistory()}
        </div>
    );
};

export default SubTicketDetailPanel;
